/**
 * ExecutionsActionBar — Dock de ações: secundárias à esquerda, conclusão à direita.
 */
import React from 'react';
import { Loader2, Paperclip, Save, TriangleAlert, UserPlus } from 'lucide-react';

interface PrimaryAction {
  label: string;
  loadingLabel: string;
  onClick: () => void | Promise<void>;
  disabled: boolean;
  busy: boolean;
  tone?: 'primary' | 'success';
  subtitle?: string | null;
}

interface ExecutionsActionBarProps {
  onAttach?: () => void;
  onSave?: () => void;
  onReportProblem?: () => void;
  onDelegate?: () => void;
  primaryAction?: PrimaryAction | null;
  saveDisabled?: boolean;
  attachDisabled?: boolean;
  problemDisabled?: boolean;
  delegateDisabled?: boolean;
  pendenciesCount?: number;
}

const SECONDARY_BTN =
  'inline-flex h-8 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 text-[10px] font-semibold text-slate-600 transition hover:bg-slate-50 disabled:opacity-40 dark:border-[var(--border-default)] dark:bg-[var(--bg-surface)] dark:text-[var(--text-secondary)]';

export const ExecutionsActionBar: React.FC<ExecutionsActionBarProps> = ({
  onAttach,
  onSave,
  onReportProblem,
  onDelegate,
  primaryAction,
  saveDisabled,
  attachDisabled,
  problemDisabled,
  delegateDisabled,
  pendenciesCount = 0,
}) => (
  <footer className="sticky bottom-0 z-20 flex shrink-0 items-stretch border-t border-slate-200 bg-white shadow-[0_-2px_12px_rgba(15,23,42,0.06)] dark:border-[var(--border-default)] dark:bg-[var(--bg-elevated)]">
    <div className="flex flex-1 flex-wrap items-center gap-1.5 px-3 py-2">
      {onAttach && (
        <button type="button" onClick={onAttach} disabled={attachDisabled} className={SECONDARY_BTN}>
          <Paperclip size={12} />
          Anexar
        </button>
      )}
      {onSave && (
        <button type="button" onClick={onSave} disabled={saveDisabled} className={SECONDARY_BTN}>
          <Save size={12} />
          Salvar
        </button>
      )}
      {onDelegate && (
        <button type="button" onClick={onDelegate} disabled={delegateDisabled} className={SECONDARY_BTN}>
          <UserPlus size={12} />
          Delegar
        </button>
      )}
      {onReportProblem && (
        <button
          type="button"
          onClick={onReportProblem}
          disabled={problemDisabled}
          className={`${SECONDARY_BTN} border-rose-200 text-rose-700 hover:bg-rose-50 dark:border-rose-900/40 dark:text-rose-300`}
        >
          <TriangleAlert size={12} />
          Problema
        </button>
      )}
      {pendenciesCount > 0 && (
        <span className="ml-1 rounded-md bg-amber-100 px-2 py-1 text-[9px] font-bold text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
          {pendenciesCount} pend.
        </span>
      )}
    </div>

    <div className="flex shrink-0 items-center gap-3 border-l border-slate-200 px-4 py-2 dark:border-[var(--border-default)]">
      {primaryAction && (
        <button
          type="button"
          onClick={() => void primaryAction.onClick()}
          disabled={primaryAction.disabled}
          title={primaryAction.subtitle ?? undefined}
          className={`inline-flex min-w-[180px] flex-col items-center justify-center rounded-md px-6 py-2.5 text-center shadow-md transition disabled:cursor-not-allowed disabled:opacity-50 ${
            primaryAction.tone === 'success'
              ? 'bg-emerald-600 text-white hover:bg-emerald-700'
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          <span className="inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.06em]">
            {primaryAction.busy && <Loader2 size={14} className="animate-spin" />}
            {primaryAction.busy ? primaryAction.loadingLabel : primaryAction.label}
          </span>
          {!primaryAction.busy && primaryAction.subtitle && (
            <span className="mt-0.5 max-w-[220px] truncate text-[8px] font-medium text-white/80">
              {primaryAction.subtitle}
            </span>
          )}
        </button>
      )}
    </div>
  </footer>
);

export default ExecutionsActionBar;
